// @ts-nocheck
/**
 * TanStack Query hooks for the Cloud Telephony Users feature.
 *
 * Wraps v1 `getApiCall`/`postApiCall` endpoints. Every mutation invalidates
 * the users list on success so downstream lists refresh without ad-hoc
 * refetches. Modeled on obd3-gui's `features/users/api.ts`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_USERS_LIST,
  API_USERS_SIGNUP,
  API_USERS_PROFILE,
  API_SIGNUP_EMAIL_SEND_CODE,
  API_SIGNUP_EMAIL_VERIFY_CODE,
} from '@/service/url';
import { BACKEND_API_BASE_URL } from '@/utilities/constant';
import { getUserAccessToken } from '@/utilities/setting';
import type {
  UserSignupPayload,
  SignupResponse,
  EmailSendCodeResponse,
  EmailVerifyCodeResponse,
  ExistingUsernameRow,
  CurrentUserProfile,
} from './types';

/* ── Query keys ─────────────────────────────────────────────────── */

export const userKeys = {
  all: ['users'] as const,
  list: () => ['users', 'list'] as const,
  existingUsernames: ['users', 'existing-usernames'] as const,
  profile: (id: string | number | null) => ['users', 'profile', id] as const,
};

function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: userKeys.all });
}

/* ── Existing usernames (for uniqueness check) ──────────────────── */

/**
 * Flat list of every existing username the creator can see. Used to
 * inline-flag "Username already taken" as the user types on Step 0.
 */
export function useExistingUsernames() {
  return useQuery({
    queryKey: userKeys.existingUsernames,
    queryFn: () => getApiCall(`${API_USERS_LIST}?role=noRole`, {}),
    staleTime: 30_000,
    select: (data: any) => {
      const rows: ExistingUsernameRow[] = Array.isArray(data) ? data : [];
      return rows.map((r) => String(r.username || '').toLowerCase()).filter(Boolean);
    },
  });
}

/* ── Current user profile (for parent-derived defaults) ─────────── */

export function useUserProfile(id: string | number | null) {
  return useQuery({
    queryKey: userKeys.profile(id),
    queryFn: () => getApiCall(API_USERS_PROFILE.replace(':id', String(id)), {}),
    enabled: Boolean(id),
    staleTime: 60_000,
    select: (data: any) => (data as CurrentUserProfile) ?? null,
  });
}

/* ── Email OTP send + verify ────────────────────────────────────── */

export function useSendSignupEmailCode() {
  return useMutation({
    mutationFn: (email: string) =>
      postApiCall(API_SIGNUP_EMAIL_SEND_CODE, { email }) as Promise<EmailSendCodeResponse>,
  });
}

export function useVerifySignupEmailCode() {
  return useMutation({
    mutationFn: ({ email, otp }: { email: string; otp: string }) =>
      postApiCall(API_SIGNUP_EMAIL_VERIFY_CODE, { email, otp }) as Promise<EmailVerifyCodeResponse>,
  });
}

/* ── Register user (create) ─────────────────────────────────────── */

export function useRegisterUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (payload: UserSignupPayload) =>
      postApiCall(API_USERS_SIGNUP, payload) as Promise<SignupResponse>,
    onSuccess: () => invalidate(),
  });
}

/* ── Update user ────────────────────────────────────────────────── */

/**
 * Update an existing user. v1 posts to the same URL as the profile GET
 * (`/api/obd/users/profile/:id`) — POST triggers the update path
 * server-side.
 */
export function useUpdateUser(userId: string | number | null) {
  const invalidate = useInvalidateUsers();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, any>) =>
      postApiCall(API_USERS_PROFILE.replace(':id', String(userId ?? '')), payload) as Promise<SignupResponse>,
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: userKeys.profile(userId) });
    },
  });
}
